import React, { useEffect, useState } from 'react';
import { RefreshCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Text from '@/utils/responsiveText';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { reactIcons } from '@/utils/icons';

interface HeaderProps {
  title: string | undefined | null;
  count?: string | number;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  showButton?: boolean;
  buttonLabel?: string;
  onButtonClick?: () => void;
  showButtonTwo?: boolean;
  buttonLabelTwo?: string;
  onButtonClickTwo?: () => void;
  resetPage?: () => void;
  showExport?: boolean;
  input?: boolean;
  search?: string;
  setSearch?: (value: string) => void;
  dashboard?: boolean;
  backbtn?: boolean;
  path?: string;
  handleExport?: (type: string) => void;
}

const Header: React.FC<HeaderProps> = ({
  title,
  count,
  isRefreshing = false,
  onRefresh,
  showButton = false,
  buttonLabel,
  onButtonClick,
  showButtonTwo = false,
  buttonLabelTwo,
  onButtonClickTwo,
  showExport = false,
  input = false,
  search,
  setSearch,
  resetPage,
  dashboard = false,
  backbtn = false,
  path,
  handleExport,
}) => {
  // const imagekit = import.meta.env.VITE_IMAGEKIT_URL;
  const navigate = useNavigate();
  const images: string[] = ['Excel', 'Pdf', 'Csv', 'refresh'];
  const iconMap: { [key: string]: React.ElementType } = {
    Excel: reactIcons.excel,
    Pdf: reactIcons.pdf,
    Csv: reactIcons.csv,
    refresh: reactIcons.refresh,
  };
  const handleExportClick = (imgName: string) => {
    if (imgName === 'refresh' && onRefresh) {
      onRefresh();
    } else {
      handleExport?.(imgName);
    }
  };
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour12: false,
  });

  return (
    <>
      {dashboard ? (
        <div
          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 
  ${title === 'DASHBOARD' ? 'padding' : 'border-b border-green3'} pb-1.5 md:pb-4`}
        >
          {/* Left Section */}
          <div className="flex items-center gap-2 min-w-0">
            {backbtn && (
              <reactIcons.circleBack
                size={28}
                onClick={() => (path ? navigate(path) : navigate(-1))}
                className="cursor-pointer shrink-0"
              />
            )}

            <Text min={20} max={28} className="font-bold text-textcolor truncate" as="h2">
              {title}
            </Text>
          </div>

          {/* Right Section */}
          <div className="flex  xs:flex-row xs:items-center justify-between sm:justify-end gap-2 xs:gap-3">
            {/* Date + Time */}
            <div className="flex items-center gap-2 text-textcolor">
              <Text min={11} max={16} className="text-xs sm:text-sm">
                {formattedDate}
              </Text>
              <Text min={16} max={24} className="text-lg sm:text-xl font-semibold">
                {formattedTime}
              </Text>
            </div>

            {/* Refresh Button */}
            <button
              onClick={onRefresh}
              className="flex items-center justify-center gap-2 bg-btn-gradient px-2 sm:px-3 py-1.5 sm:py-2 rounded-full border text-black text-xs sm:text-sm w-24 xs:w-auto"
            >
              <RefreshCcw
                size={16}
                className={`transition-transform duration-700 ${
                  isRefreshing ? 'animate-spin' : ''
                }`}
              />
              <span className="hidden xs:inline">Refresh</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-green3 pb-4 text-textcolor">
          {/* LEFT */}
          <div className="flex items-center gap-2 min-w-0">
            {backbtn && (
              <reactIcons.circleBack
                size={28}
                onClick={() => (path ? navigate(path) : navigate(-1))}
                className="cursor-pointer shrink-0"
              />
            )}

            <Text min={16} max={24} className="font-semibold" as="h3">
              {title}
            </Text>

            {count && (
              <Text min={12} max={16} as="span" className="shrink-0">
                {count}
              </Text>
            )}
          </div>

          {/* RIGHT */}
          <div className="flex flex-col xs:flex-row xs:flex-wrap sm:flex-nowrap items-stretch xs:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Top Row (Refresh + Button + Export) */}
            <div className="flex flex-wrap items-center gap-2 justify-between ">
              {/* Refresh */}
              {onRefresh && !showExport && (
                <div className="border-green rounded-md bg-white p-1 shrink-0">
                  <RefreshCcw
                    onClick={onRefresh}
                    className={`h-6 w-6 sm:h-7 sm:w-7 transition-transform duration-700 cursor-pointer text-black ${
                      isRefreshing ? 'animate-spin' : ''
                    }`}
                  />
                </div>
              )}

              {/* Create Button */}
              {showButton && (
                <Button onClick={onButtonClick} className="">
                  <Text min={13} max={16} className="font-semibold">
                    {buttonLabel || `+ Create ${title}`}
                  </Text>
                </Button>
              )}
              {showButtonTwo && (
                <Button onClick={onButtonClickTwo} className="">
                  <Text min={13} max={16} className="font-semibold">
                    {buttonLabelTwo || `+ Create ${title}`}
                  </Text>
                </Button>
              )}

              {/* Export Icons */}
              {showExport && (
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  {images.map((imgName) => {
                    const IconComponent = iconMap[imgName];
                    if (!IconComponent) return null;

                    return (
                      <div
                        key={imgName}
                        className="border-green p-1 rounded-sm"
                        onClick={() => handleExportClick(imgName)}
                      >
                        <IconComponent
                          className={`cursor-pointer ${
                            imgName === 'refresh' && isRefreshing ? 'animate-spin' : ''
                          }`}
                          size={16}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Search Input */}
            {input && (
              <div className="relative w-full sm:w-[220px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search..."
                  className="bg-transparent pl-9 text-dark-gray border-green focus-visible:ring-green text-textcolor text-sm"
                  value={search}
                  onChange={(e) => {
                    setSearch?.(e.target.value);
                    resetPage?.();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
